import React, { useState, useRef, useEffect, useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { fetchUserById } from '../../../../api/administration/users';
import { useUsers } from '../../../../hooks/admin/userManagement/useUsers';
import { TO_UFS_INPUT_CLASS } from '../constants';
import { getUserDisplayWithJobTitle } from '../helpers/userDisplay';

/**
 * Single input with user search suggestions dropdown (type to search, click to select).
 * Stores and emits user ID; displays name via valueDisplay.
 * When the form holds a user id but no label (workflow defaults hydrate before display names sync),
 * the label is fetched once via {@link fetchUserById} so the field is not visually empty.
 *
 * @param {string} value - User ID (or empty string)
 * @param {string} [valueDisplay] - Name to show in the input when value is an ID
 * @param {function(id: string, displayName: string): void} onChange - (id, displayName) when user selects
 */
export function UserSearchInput({
  value,
  valueDisplay,
  onChange,
  placeholder,
  disabled = false,
  error,
  inputClassName = TO_UFS_INPUT_CLASS,
  'aria-invalid': ariaInvalid,
  /** e.g. when parent omits visible labels (verification slots). */
  'aria-label': ariaLabelProp,
}) {
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  const idTrim = String(value ?? '').trim();
  const labelTrim = String(valueDisplay ?? '').trim();

  const userByIdQuery = useQuery({
    queryKey: ['user-search-input-label', idTrim],
    queryFn: () => fetchUserById(idTrim),
    enabled: Boolean(idTrim && !labelTrim),
    staleTime: 5 * 60 * 1000,
  });

  const fetchedLabel =
    userByIdQuery.data && typeof userByIdQuery.data === 'object'
      ? String(getUserDisplayWithJobTitle(userByIdQuery.data) ?? '').trim()
      : '';

  /** Effective picker label once parent + optional API lookup agree */
  const effectiveLabel = labelTrim || fetchedLabel;
  /** We have an id wired from RHF; parent may omit display until enrichment / role resolution settles */
  const hasStoredUserId = Boolean(idTrim);
  /** RQ v5: pending while the by-id lookup for this field is in flight */
  const awaitingFetchedLabel =
    hasStoredUserId &&
    !labelTrim &&
    !fetchedLabel &&
    (userByIdQuery.isPending || userByIdQuery.isFetching);
  const loadFailed =
    hasStoredUserId &&
    !labelTrim &&
    !fetchedLabel &&
    userByIdQuery.isFetched &&
    !userByIdQuery.isFetching &&
    (userByIdQuery.isError || userByIdQuery.data == null);

  const inputDisplay =
    searchTerm.trim().length > 0
      ? searchTerm
      : hasStoredUserId
        ? effectiveLabel ||
          (awaitingFetchedLabel ? 'Loading…' : '') ||
          (loadFailed ? `User ${idTrim.slice(-6)}` : '')
        : searchTerm;
  const { users = [], isLoading: usersLoading } = useUsers(
    { search: searchTerm.trim(), limit: 10, isActive: true },
    { enabled: searchTerm.trim().length >= 2 }
  );

  useEffect(() => {
    if (!suggestionsOpen) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [suggestionsOpen]);

  const handleChange = (e) => {
    const next = e.target.value;
    setSearchTerm(next);
    if (hasStoredUserId) onChange('', '');
    setSuggestionsOpen(next.trim().length >= 2);
  };

  const handleFocus = () => {
    if (searchTerm.trim().length >= 2) setSuggestionsOpen(true);
  };

  const handleSelectUser = (user) => {
    const id = user._id || user.id;
    const displayName = getUserDisplayWithJobTitle(user);
    onChange(id, displayName);
    setSearchTerm('');
    setSuggestionsOpen(false);
  };

  const showSuggestions = suggestionsOpen && searchTerm.trim().length >= 2;
  const listboxId = useId();

  return (
    <div className="relative min-w-0 space-y-1" ref={wrapperRef}>
      <input
        type="text"
        value={inputDisplay}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={inputClassName}
        role="combobox"
        aria-label={ariaLabelProp}
        aria-invalid={ariaInvalid ?? !!error}
        aria-autocomplete="list"
        aria-expanded={showSuggestions && users.length > 0}
        aria-controls={showSuggestions ? listboxId : undefined}
      />
      {showSuggestions && !disabled && (
        <div
          id={listboxId}
          className="absolute z-50 mt-0.5 w-full min-w-[12rem] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          role="listbox"
        >
          {usersLoading ? (
            <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading...
            </div>
          ) : users.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">No users found</div>
          ) : (
            users.map((user) => {
              const name = getUserDisplayWithJobTitle(user);
              return (
                <button
                  key={user._id || user.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                  onClick={() => handleSelectUser(user)}
                >
                  {name}
                </button>
              );
            })
          )}
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
